// src/pages/admin/RequestDetail.js
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import { db } from "../../lib/firebase";
import { useAuth } from "../../context/AuthContext";
import { STATUS_OPTIONS } from "./StatusOptions";
import allocateQuoteNumber from "../../lib/quoteNumbers";

function formatDate(ts) {
  if (!ts) return "—";
  const d = ts?.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleString();
}

function truncate(str, n = 160) {
  if (!str) return "";
  const s = String(str).trim();
  return s.length > n ? s.slice(0, n) + "…" : s;
}

export default function RequestDetail() {
  const { id } = useParams();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  // Notes
  const [notes, setNotes] = useState([]);
  const [noteText, setNoteText] = useState("");
  const [noteBusy, setNoteBusy] = useState(false);

  // Client linking
  const [clientDoc, setClientDoc] = useState(null);
  const [clientSearch, setClientSearch] = useState("");
  const [clients, setClients] = useState([]);
  const [linkBusy, setLinkBusy] = useState(false);

  // Quotes (creation only for now)
  const [quoteBusy, setQuoteBusy] = useState(false);
  const [createdQuote, setCreatedQuote] = useState(null);

  const attachments = useMemo(
    () => (Array.isArray(data?.attachments) ? data.attachments : []),
    [data]
  );

  /* -------------------- Load request -------------------- */
  useEffect(() => {
    if (!id) return;

    const ref = doc(db, "evaluationRequests", id);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const next = snap.exists() ? { id: snap.id, ...snap.data() } : null;
        setData(next);
        setLoading(false);
      },
      () => {
        setData(null);
        setLoading(false);
      }
    );

    return () => unsub();
  }, [id]);

  /* -------------------- Load notes -------------------- */
  useEffect(() => {
    if (!id) return;

    const q = query(
      collection(db, "evaluationRequests", id, "notes"),
      orderBy("createdAt", "desc"),
      limit(200)
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        setNotes(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      },
      () => setNotes([])
    );

    return () => unsub();
  }, [id]);

  /* -------------------- Load clients (for linking search) -------------------- */
  useEffect(() => {
    const q = query(collection(db, "clients"), orderBy("createdAt", "desc"), limit(200));
    const unsub = onSnapshot(
      q,
      (snap) => setClients(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      () => setClients([])
    );
    return () => unsub();
  }, []);

  /* -------------------- Load linked client doc -------------------- */
  useEffect(() => {
    let active = true;

    async function loadLinkedClient() {
      const clientId = data?.clientId;
      if (!clientId) {
        setClientDoc(null);
        return;
      }

      try {
        const snap = await getDoc(doc(db, "clients", clientId));
        if (!active) return;
        setClientDoc(snap.exists() ? { id: snap.id, ...snap.data() } : null);
      } catch {
        if (!active) return;
        setClientDoc(null);
      }
    }

    loadLinkedClient();

    return () => {
      active = false;
    };
  }, [data?.clientId]);

  /* -------------------- Actions -------------------- */
  async function updateStatus(nextStatus) {
    if (!id) return;

    setData((prev) => (prev ? { ...prev, status: nextStatus } : prev));

    await updateDoc(doc(db, "evaluationRequests", id), {
      status: nextStatus,
      lastUpdatedAt: serverTimestamp(),
    });

    await addDoc(collection(db, "evaluationRequests", id, "notes"), {
      createdAt: serverTimestamp(),
      text: `Status changed to: ${nextStatus}`,
      authorUid: user?.uid || null,
      authorEmail: user?.email || null,
      system: true,
    });
  }

  async function addNote(e) {
    e.preventDefault();
    const text = noteText.trim();
    if (!text || !id) return;

    setNoteBusy(true);
    try {
      await addDoc(collection(db, "evaluationRequests", id, "notes"), {
        createdAt: serverTimestamp(),
        text,
        authorUid: user?.uid || null,
        authorEmail: user?.email || null,
      });

      await updateDoc(doc(db, "evaluationRequests", id), {
        lastNoteText: text,
        lastNoteAt: serverTimestamp(),
        lastUpdatedAt: serverTimestamp(),
      });

      setNoteText("");
    } finally {
      setNoteBusy(false);
    }
  }

  async function createClientFromRequest() {
    if (!id || !data) return;

    setLinkBusy(true);
    try {
      const name = (data.name || "").trim();
      const email = (data.email || "").trim();
      const phone = (data.phone || "").trim();

      const clientRef = doc(collection(db, "clients"));
      await setDoc(clientRef, {
        createdAt: serverTimestamp(),
        lastUpdatedAt: serverTimestamp(),
        name: name || "Client",
        email: email || "",
        phone: phone || "",
        organization: "",
        notes: "",
        sourceRequestId: id,

        // location fields
        country: "",
        region: "",
        city: "",
      });

      await updateDoc(doc(db, "evaluationRequests", id), {
        clientId: clientRef.id,
        lastUpdatedAt: serverTimestamp(),
      });

      await addDoc(collection(db, "evaluationRequests", id, "notes"), {
        createdAt: serverTimestamp(),
        text: `Linked to new client: ${clientRef.id}`,
        authorUid: user?.uid || null,
        authorEmail: user?.email || null,
        system: true,
      });
    } finally {
      setLinkBusy(false);
    }
  }

  async function linkToExistingClient(clientId) {
  if (!id || !clientId) return;

  setLinkBusy(true);
  try {
    // fetch client
    let c = null;
    const cSnap = await getDoc(doc(db, "clients", clientId));
    if (cSnap.exists()) c = cSnap.data();

    // link + backfill request display fields
    await updateDoc(doc(db, "evaluationRequests", id), {
      clientId,
      name: c?.name || "",
      email: c?.email || "",
      phone: c?.phone || "",
      lastUpdatedAt: serverTimestamp(),
    });

    await addDoc(collection(db, "evaluationRequests", id, "notes"), {
      createdAt: serverTimestamp(),
      text: `Linked to existing client: ${clientId}${c?.name ? ` (${c.name})` : ""}`,
      authorUid: user?.uid || null,
      authorEmail: user?.email || null,
      system: true,
    });
  } finally {
    setLinkBusy(false);
  }
}


  async function createQuote() {
    if (!id || !data) return;

    setQuoteBusy(true);
    try {
      // Pull client address-like fields if available.
      // NOTE: Your client doc currently stores country/region/city.
      // If you later add addressLine1/addressLine2/postalCode, this will automatically use them.
      let clientAddress = {
  line1: "",
  line2: "",
  city: "",
  region: "",
  postalCode: "",
  country: "",
};

let clientName = (data.name || "").trim();
let clientEmail = (data.email || "").trim();
let clientPhone = (data.phone || "").trim();
let clientOrganization = "";

if (data.clientId) {
  const cSnap = await getDoc(doc(db, "clients", data.clientId));
  if (cSnap.exists()) {
    const c = cSnap.data();

    clientName = (c.name || clientName).trim();
    clientEmail = (c.email || clientEmail).trim();
    clientPhone = (c.phone || clientPhone).trim();
    clientOrganization = c.organization || "";

    clientAddress = {
      line1: c.addressLine1 || "",
      line2: c.addressLine2 || "",
      city: c.city || "",
      region: c.region || "",
      postalCode: c.postalCode || "",
      country: c.country || "",
    };
  }
}


      const quoteNumber = await allocateQuoteNumber("EV");
      const quoteRef = doc(collection(db, "quotes")); // auto-id quoteId

      await setDoc(quoteRef, {
        requestId: id,
        clientId: data.clientId || null,
        createdByUid: user?.uid || null,
        createdByEmail: user?.email || null,

        quoteNumber,
        title: "Restoration Quote",

        clientName: (data.name || "").trim(),
        clientOrganization,
        clientEmail: (data.email || "").trim(),
        clientPhone: (data.phone || "").trim(),

        clientAddress,

        items: [],

        discount: { type: "amount", value: 0 },
        shipping: { type: "amount", value: 0 },
        tax: { type: "percent", value: 0 },

        totals: {
          itemsSubtotal: 0,
          discountAmount: 0,
          shippingAmount: 0,
          preTaxSubtotal: 0,
          taxAmount: 0,
          grandTotal: 0,
        },

        publicMessage: "",
        internalNotes: "",

        status: "draft",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        sentAt: null,
        lastViewedAt: null,

        publicEnabled: true,
      });

      await addDoc(collection(db, "evaluationRequests", id, "notes"), {
        createdAt: serverTimestamp(),
        text: `Quote created: ${quoteNumber} (${quoteRef.id})`,
        authorUid: user?.uid || null,
        authorEmail: user?.email || null,
        system: true,
      });

      setCreatedQuote({ id: quoteRef.id, quoteNumber });
    } finally {
      setQuoteBusy(false);
    }
  }

  /* -------------------- Derived -------------------- */
  const searchResults = useMemo(() => {
    const needle = clientSearch.trim().toLowerCase();
    if (!needle) return [];

    return clients
      .filter((c) => {
        const blob = [c.name, c.email, c.phone, c.organization, c.city, c.region, c.country]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return blob.includes(needle);
      })
      .slice(0, 10);
  }, [clientSearch, clients]);

  /* -------------------- UI -------------------- */
  if (loading) return <p className="muted">Loading…</p>;

  if (!data) {
    return (
      <div>
        <p className="muted">Request not found.</p>
        <Link className="btn" to="/admin">
          Back
        </Link>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div>
          <h2 style={{ marginBottom: 6 }}>{data.name || "Request"}</h2>
          <div className="micro muted">
            {data.email || "—"} • {data.phone || "—"}
          </div>
        </div>

        <div style={{ textAlign: "right", minWidth: 220 }}>
          <div className="micro muted">{formatDate(data.createdAt)}</div>

          <div style={{ marginTop: 10 }}>
            <div className="micro muted" style={{ marginBottom: 6 }}>
              Status
            </div>
            <select
              value={data.status || "new"}
              onChange={(e) => updateStatus(e.target.value)}
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: 12,
                border: "1px solid rgba(0,0,0,0.15)",
                background: "rgba(255,255,255,0.7)",
              }}
            >
              {STATUS_OPTIONS.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Client linking */}
      <div style={{ marginTop: 18 }}>
        <h3 style={{ marginBottom: 8 }}>Client</h3>

        {data.clientId && clientDoc ? (
          <div
            style={{
              border: "1px solid rgba(0,0,0,0.1)",
              borderRadius: 14,
              padding: 14,
              background: "rgba(255,255,255,0.7)",
              display: "flex",
              justifyContent: "space-between",
              gap: 12,
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            <div>
              <strong style={{ display: "block" }}>{clientDoc.name || "Client"}</strong>
              <div className="micro muted" style={{ marginTop: 4 }}>
                {clientDoc.email || "—"} • {clientDoc.phone || "—"}
              </div>
              {(clientDoc.city || clientDoc.region || clientDoc.country) && (
                <div className="micro muted" style={{ marginTop: 4 }}>
                  {[clientDoc.city, clientDoc.region, clientDoc.country].filter(Boolean).join(", ")}
                </div>
              )}
            </div>

            <Link className="btn primary" to={`/admin/clients/${data.clientId}`}>
              View Client
            </Link>
          </div>
        ) : (
          <div
            style={{
              border: "1px solid rgba(0,0,0,0.1)",
              borderRadius: 14,
              padding: 14,
              background: "rgba(255,255,255,0.7)",
            }}
          >
            <p className="micro muted" style={{ marginTop: 0 }}>
              This request is not linked to a client yet.
            </p>

            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button className="btn primary" type="button" onClick={createClientFromRequest} disabled={linkBusy}>
                {linkBusy ? "Working…" : "Create Client From Request"}
              </button>
            </div>

            <div style={{ marginTop: 14 }}>
              <div className="micro muted" style={{ marginBottom: 6 }}>
                Or link to existing client
              </div>

              <input
                type="text"
                placeholder="Search clients by name, email, phone…"
                value={clientSearch}
                onChange={(e) => setClientSearch(e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: 12,
                  border: "1px solid rgba(0,0,0,0.15)",
                  background: "rgba(255,255,255,0.75)",
                }}
              />

              {clientSearch.trim() && (
                <div style={{ marginTop: 10, display: "grid", gap: 10 }}>
                  {searchResults.length === 0 && (
                    <p className="micro muted" style={{ margin: 0 }}>
                      No clients found.
                    </p>
                  )}

                  {searchResults.map((c) => (
                    <div
                      key={c.id}
                      style={{
                        border: "1px solid rgba(0,0,0,0.1)",
                        borderRadius: 14,
                        padding: 12,
                        background: "rgba(255,255,255,0.65)",
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 10,
                        flexWrap: "wrap",
                        alignItems: "center",
                      }}
                    >
                      <div>
                        <strong style={{ display: "block" }}>{c.name || "Client"}</strong>
                        <div className="micro muted" style={{ marginTop: 4 }}>
                          {c.email || "—"} • {c.phone || "—"}
                        </div>
                        {(c.city || c.region || c.country) && (
                          <div className="micro muted" style={{ marginTop: 4 }}>
                            {[c.city, c.region, c.country].filter(Boolean).join(", ")}
                          </div>
                        )}
                      </div>

                      <button
                        type="button"
                        className="btn"
                        onClick={() => linkToExistingClient(c.id)}
                        disabled={linkBusy}
                      >
                        Link
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Quotes */}
      <div style={{ marginTop: 18 }}>
        <h3 style={{ marginBottom: 8 }}>Quotes</h3>

        <button type="button" className="btn primary" onClick={createQuote} disabled={quoteBusy}>
          {quoteBusy ? "Creating…" : "Create Quote"}
        </button>

        {createdQuote && (
          <div
            style={{
              marginTop: 10,
              border: "1px solid rgba(0,0,0,0.1)",
              borderRadius: 14,
              padding: 12,
              background: "rgba(255,255,255,0.7)",
            }}
          >
            <div className="micro muted" style={{ marginBottom: 6 }}>
              Created {createdQuote.quoteNumber}
            </div>

            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <a
                className="btn"
                href={`${window.location.origin}/quote/${createdQuote.id}`}
                target="_blank"
                rel="noreferrer"
              >
                View Public Link
              </a>

              <Link className="btn primary" to={`/admin/quotes/${createdQuote.id}`}>
                Open Quote Editor
              </Link>
            </div>

            <div className="micro muted" style={{ marginTop: 8 }}>
              Public URL: <code>{`${window.location.origin}/quote/${createdQuote.id}`}</code>
            </div>
          </div>
        )}
      </div>

      {/* Message */}
      {data.message && (
        <div style={{ marginTop: 18 }}>
          <h3 style={{ marginBottom: 8 }}>Message</h3>
          <div
            style={{
              border: "1px solid rgba(0,0,0,0.1)",
              borderRadius: 14,
              padding: 14,
              background: "rgba(255,255,255,0.7)",
              whiteSpace: "pre-wrap",
            }}
          >
            {data.message}
          </div>
        </div>
      )}

      {/* Attachments */}
      <div style={{ marginTop: 18 }}>
        <h3 style={{ marginBottom: 8 }}>
          Attachments {attachments.length ? `(${attachments.length})` : ""}
        </h3>

        {attachments.length === 0 && <p className="muted">No photos attached.</p>}

        {attachments.length > 0 && (
          <div
            style={{
              display: "grid",
              gap: 12,
              gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
            }}
          >
            {attachments.map((a, idx) => (
              <a
                key={a.url || idx}
                href={a.url}
                target="_blank"
                rel="noreferrer"
                style={{
                  display: "block",
                  border: "1px solid rgba(0,0,0,0.1)",
                  borderRadius: 14,
                  overflow: "hidden",
                  background: "rgba(255,255,255,0.7)",
                  textDecoration: "none",
                  color: "inherit",
                }}
              >
                <div style={{ aspectRatio: "1/1", background: "#eee" }}>
                  <img
                    src={a.url}
                    alt={a.name || `Photo ${idx + 1}`}
                    loading="lazy"
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                </div>
                <div className="micro muted" style={{ padding: 10 }}>
                  {a.name || `Photo ${idx + 1}`}
                </div>
              </a>
            ))}
          </div>
        )}
      </div>

      {/* Notes */}
      <div style={{ marginTop: 22 }}>
        <h3 style={{ marginBottom: 8 }}>Notes</h3>

        <form onSubmit={addNote} className="form" style={{ marginBottom: 12 }}>
          <label className="field">
            <textarea
              rows="4"
              placeholder="Add a note… (calls, callbacks, voicemails, decisions, etc.)"
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              disabled={noteBusy}
            />
          </label>
          <button className="btn primary" disabled={noteBusy || !noteText.trim()}>
            {noteBusy ? "Saving…" : "Add Note"}
          </button>
        </form>

        {notes.length === 0 && <p className="muted">No notes yet.</p>}

        {notes.length > 0 && (
          <div style={{ display: "grid", gap: 10 }}>
            {notes.map((n) => (
              <div
                key={n.id}
                style={{
                  border: "1px solid rgba(0,0,0,0.1)",
                  borderRadius: 14,
                  padding: 12,
                  background: "rgba(255,255,255,0.65)",
                }}
              >
                <div
                  className="micro muted"
                  style={{ display: "flex", justifyContent: "space-between", gap: 10 }}
                >
                  <span>{n.authorEmail || "Admin"}</span>
                  <span>{formatDate(n.createdAt)}</span>
                </div>

                <div style={{ marginTop: 8, whiteSpace: "pre-wrap" }}>{n.text}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={{ marginTop: 18, display: "flex", gap: 10, flexWrap: "wrap" }}>
        <Link className="btn" to="/admin">
          Back to Dashboard
        </Link>
        {data.clientId && (
          <Link className="btn" to={`/admin/clients/${data.clientId}`}>
            View Client
          </Link>
        )}
      </div>
    </div>
  );
}
